import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { useApolloClient } from '@apollo/client/react';
import { appendToProjectState, identifyProject, nextOrderedIndex, ORDERED_ENTITY_FRAGMENT } from './optimisticProjectState';
import { useProjectCreateAnnotationMutation, PropertyValueType as GqlPropertyValueType, PropertyType as GqlPropertyType } from '../../types/graphqlTypes';
import { PropertyType, PropertyValueType, PropertyValueData } from '@pointorama/potree-shared';
import { getGlobalPropertyService } from '@pointorama/potree-shared';
import { buildAnnotationCacheEntry, type CreateAnnotationParams, type InitialProperty } from './annotationCacheUtils';
import { scheduleCreateMutation } from './annotationMutationCoalescer';
import { clientId } from '../pusher';

export type { InitialProperty, CreateAnnotationParams };

const ANNOTATION_ENTITY_FRAGMENT = gql`
  fragment NewAnnotationEntity on Annotation {
    identifier
    type
    groupIdentifier
  }
`;

/** Maps potree-shared PropertyType to GraphQL PropertyType */
const PROPERTY_TYPE_MAP: Record<string, GqlPropertyType> = {
  [PropertyType.PROJECT_GLOBAL]: GqlPropertyType.GlobalState,
  [PropertyType.PROJECT_USER]: GqlPropertyType.StateByUser,
  [PropertyType.ORGANIZATION]: GqlPropertyType.Organization,
  [PropertyType.USER_SETTINGS]: GqlPropertyType.User,
};

/** Maps potree-shared PropertyValueType to GraphQL PropertyValueType */
const VALUE_TYPE_MAP: Record<string, GqlPropertyValueType> = {
  [PropertyValueType.FLOAT]: GqlPropertyValueType.Float,
  [PropertyValueType.INT]: GqlPropertyValueType.Int,
  [PropertyValueType.STRING]: GqlPropertyValueType.String,
  [PropertyValueType.BOOLEAN]: GqlPropertyValueType.Boolean,
  [PropertyValueType.SELECT]: GqlPropertyValueType.Select,
  [PropertyValueType.COLOR]: GqlPropertyValueType.Color,
  [PropertyValueType.COORDINATE]: GqlPropertyValueType.Coordinate,
  [PropertyValueType.JSON]: GqlPropertyValueType.Json,
  [PropertyValueType.FLOAT_ARRAY]: GqlPropertyValueType.FloatArray,
};

export function useCreateAnnotation() {
  const { projectId } = useParams<{ projectId: string }>();
  const [createAnnotationMutation] = useProjectCreateAnnotationMutation();
  const apolloClient = useApolloClient();

  const createAnnotation = useCallback(
    async ({ identifier, type, groupIdentifier, initialProperties }: CreateAnnotationParams) => {
      if (!projectId) return;

      // Write initial properties to PropertyService immediately (optimistic local update).
      // Use setValuesLocally to skip broadcasts and debounced saves — the mutation
      // already persists these values server-side. Local subscribers are still notified.
      const propertyService = getGlobalPropertyService();
      if (propertyService && initialProperties) {
        propertyService.setValuesLocally(
          initialProperties.map((prop) => ({
            identifier: prop.identifier,
            objectIdentifier: prop.objectIdentifier,
            propertyType: prop.propertyType,
            value: prop.value as PropertyValueData,
          })),
        );
      }

      // Optimistically add the annotation to the Apollo cache so the panel
      // renders it immediately. Deliberately uses cache.modify + fragments,
      // NOT read/writeQuery of the full annotation-state document: a strict
      // full-query read silently returns null when ANY selected field is
      // missing anywhere in the cache, which skipped the whole optimistic
      // write and made creations appear a full round-trip late.
      const params: CreateAnnotationParams = { identifier, type, groupIdentifier, initialProperties };
      try {
        const cache = apolloClient.cache;
        const projectRef = identifyProject(cache, projectId);
        if (projectRef) {
          const annotationRef = cache.writeFragment({ data: buildAnnotationCacheEntry(params), fragment: ANNOTATION_ENTITY_FRAGMENT });
          const orderedRef = cache.writeFragment({
            data: { __typename: 'orderedIdentifier' as const, identifier, index: nextOrderedIndex(cache, projectRef) },
            fragment: ORDERED_ENTITY_FRAGMENT,
          });
          appendToProjectState(cache, projectRef, { identifier, annotation: annotationRef, ordered: orderedRef });

          // Client-level re-write of the entity — a data no-op — triggers the
          // client query broadcast so the panel renders it in the same tick.
          apolloClient.writeFragment({ data: buildAnnotationCacheEntry(params), fragment: ANNOTATION_ENTITY_FRAGMENT });
        }
      } catch (err) {
        // The mutation response (full ProjectState) still reconciles, but the
        // annotation appears a round-trip late — surface the cause.
        console.error('[createAnnotation] optimistic cache update failed:', err);
      }

      // Map properties to GraphQL input format
      const gqlProperties = initialProperties?.map((prop) => ({
        identifier: prop.identifier,
        propertyIdentifier: prop.objectIdentifier,
        value: prop.value,
        valueType: VALUE_TYPE_MAP[prop.valueType] || GqlPropertyValueType.String,
        propertyType: PROPERTY_TYPE_MAP[prop.propertyType] || GqlPropertyType.GlobalState,
      }));

      // Defer the mutation through the coalescer so a rapid create/delete
      // pair (e.g. from spammed Ctrl+Z / Ctrl+Shift+Z across an area point
      // removal) cancels both server round-trips. Local properties + cache
      // are already set, so the UI stays in sync regardless.
      scheduleCreateMutation(identifier, () =>
        createAnnotationMutation({
          variables: {
            projectId,
            identifier,
            type,
            groupIdentifier: groupIdentifier || undefined,
            initialProperties: gqlProperties,
            clientId,
          },
        }),
      );
    },
    [projectId, createAnnotationMutation, apolloClient],
  );

  return createAnnotation;
}
